import { useCallback, useEffect, useId, useRef, useState } from 'react';

export type ExpandableText = {
  /** Goes on the clamped element, so the hook can measure it. */
  ref: (node: HTMLElement | null) => void;
  /** Whether the clamp is currently off. */
  expanded: boolean;
  /**
   * Whether the text is actually longer than the clamp allows.
   *
   * False until it has been measured, which is deliberate: the toggle is not
   * in the prerendered HTML and appears once the page knows there is
   * something to reveal. Of the 35 clamped ledes on the site, 12 overflow two
   * lines at 390px — so a toggle rendered unconditionally, or a row reserved
   * for one, would be dead space on two pages out of three.
   */
  truncated: boolean;
  toggle: () => void;
  /** The clamped element's id, for the toggle's `aria-controls`. */
  contentId: string;
};

/**
 * The state behind a "Read more": whether the clamp is off, and whether there
 * is anything behind it worth a button.
 *
 * The second half is the part that has to be measured rather than decided.
 * `-webkit-line-clamp` cuts at a rendered line, so whether a given sentence is
 * cut at all depends on the width it is set at, the font that actually loaded
 * and the language — none of which are known when this site's HTML is written
 * (it prerenders with `ssr: false`). A character count would be a guess about
 * the same thing, and it would be wrong at exactly the widths it matters.
 *
 * So: `scrollHeight > clientHeight` on the clamped element, re-run whenever
 * the element changes size. A `ResizeObserver` and not a resize listener,
 * because the thing that changes is usually the element and not the window —
 * a font swapping in reflows the text without the viewport moving at all.
 *
 * The measurement is skipped while expanded, and that is the whole trick:
 * with the clamp off the element is exactly as tall as its content, so a
 * fresh measurement would report "not truncated" and take the button away
 * while the visitor was reading — leaving them expanded with no way back.
 * `truncated` keeps the answer it had when the text was clamped.
 */
export function useExpandableText(): ExpandableText {
  const contentId = useId();
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const nodeRef = useRef<HTMLElement | null>(null);

  // A callback ref, so the measurement starts as soon as the element exists
  // rather than one render later. The element is also conditional on the page
  // having a lede at all, so it can arrive and leave.
  const ref = useCallback((node: HTMLElement | null) => {
    nodeRef.current = node;
  }, []);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || expanded) return;

    const measure = () => setTruncated(node.scrollHeight > node.clientHeight + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [expanded]);

  const toggle = useCallback(() => setExpanded((v) => !v), []);

  return { ref, expanded, truncated, toggle, contentId };
}
